import React, { useState, useCallback, useEffect } from 'react'
import { trpc } from '../../lib/trpc'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Checkbox } from '../ui/checkbox'
import { Progress } from '../ui/progress'
import { Badge } from '../ui/badge'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible'
import { Alert, AlertDescription } from '../ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs'
import { Separator } from '../ui/separator'
import { ChevronDownIcon, ChevronRightIcon, RefreshCwIcon, AlertTriangleIcon, CheckCircleIcon, XCircleIcon, ClockIcon } from 'lucide-react'
import { debounce } from 'lodash'

interface ValidationResult {
  valid: boolean
  errors: string[]
  warnings: string[]
}

interface ValidationState {
  [key: string]: ValidationResult
}

interface ConnectionConfig {
  serviceType: 'jetsy' | 'shopify' | 'custom'
  displayName: string
  hostUrl: string
  authType: 'none' | 'api-key' | 'oauth'
  apiKey?: string
  testConnection?: boolean
}

interface ImportConfig {
  targetWorkspaceId: string
  createNewBoard: boolean
  boardName?: string
  enableDryRun: boolean
  maxRecords: number
  batchSize: number
  skipExisting: boolean
  maxImagesPerTask: number
  enableImageDownload: boolean
}

interface ImportHistory {
  id: string
  timestamp: Date
  status: 'success' | 'partial' | 'failed'
  recordsCreated: number
  recordsUpdated: number
  recordsFailed: number
  duration: string
  triggerType: 'manual' | 'scheduled' | 'webhook'
  canRollback: boolean
}

interface ExternalDataIntegrationProps {
  workspaceId: string
}

export function ExternalDataIntegration({ workspaceId }: ExternalDataIntegrationProps) {
  const [currentStep, setCurrentStep] = useState<'connection' | 'config' | 'mapping' | 'schedule'>('connection')
  const [connectionConfig, setConnectionConfig] = useState<ConnectionConfig>({
    serviceType: 'custom',
    displayName: '',
    hostUrl: '',
    authType: 'none'
  })
  const [importConfig, setImportConfig] = useState<ImportConfig>({
    targetWorkspaceId: workspaceId,
    createNewBoard: true,
    enableDryRun: true,
    maxRecords: 100,
    batchSize: 10,
    skipExisting: true,
    maxImagesPerTask: 5,
    enableImageDownload: true
  })
  const [validation, setValidation] = useState<ValidationState>({})
  const [connectionStatus, setConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected' | 'error'>('disconnected')
  const [isImporting, setIsImporting] = useState(false)
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0, percent: 0 })
  const [selectedRecords, setSelectedRecords] = useState<Set<string>>(new Set())
  const [previewData, setPreviewData] = useState<any[]>([])
  const [importHistory, setImportHistory] = useState<ImportHistory[]>([])
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['basic']))

  // Real-time validation
  const validateField = useCallback(debounce((field: string, value: any) => {
    const errors: string[] = []
    const warnings: string[] = []
    
    switch (field) {
      case 'displayName':
        if (!value || value.trim().length === 0) errors.push('Display name is required')
        else if (value.length > 50) errors.push('Display name must be 50 characters or less')
        break
        
      case 'hostUrl':
        if (!value) errors.push('URL is required')
        else if (!isValidUrl(value)) errors.push('Invalid URL format')
        else if (!value.startsWith('https://')) warnings.push('HTTP connections are less secure')
        break
        
      case 'apiKey':
        if (connectionConfig.authType === 'api-key' && !value) errors.push('API key is required')
        else if (value && value.length < 10) warnings.push('API key seems short')
        break
        
      case 'maxRecords':
        if (value > 10000) warnings.push('Large imports may take several minutes')
        if (value > 50000) errors.push('Maximum 50,000 records allowed')
        if (value < 1) errors.push('Must import at least 1 record')
        break
        
      case 'batchSize':
        if (value > 100) warnings.push('Large batch sizes may cause timeouts')
        if (value < 1) errors.push('Batch size must be at least 1')
        break
    }
    
    setValidation(prev => ({
      ...prev,
      [field]: { errors, warnings, valid: errors.length === 0 }
    }))
  }, 300), [connectionConfig])

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  const handleConnectionTest = async () => {
    setConnectionStatus('connecting')
    try {
      // Simulate API call - replace with actual implementation
      await new Promise(resolve => setTimeout(resolve, 2000))
      setConnectionStatus('connected')
      setExpandedSections(prev => new Set([...prev, 'import-settings']))
    } catch (error) {
      setConnectionStatus('error')
    }
  }

  const handleImport = async () => {
    setIsImporting(true)
    setImportProgress({ current: 0, total: importConfig.maxRecords, percent: 0 })
    
    try {
      // Simulate import progress
      for (let i = 0; i <= importConfig.maxRecords; i += importConfig.batchSize) {
        await new Promise(resolve => setTimeout(resolve, 500))
        const current = Math.min(i + importConfig.batchSize, importConfig.maxRecords)
        const percent = (current / importConfig.maxRecords) * 100
        setImportProgress({ current, total: importConfig.maxRecords, percent })
      }
      
      // Add to history
      const newImport: ImportHistory = {
        id: Date.now().toString(),
        timestamp: new Date(),
        status: 'success',
        recordsCreated: 85,
        recordsUpdated: 15,
        recordsFailed: 0,
        duration: '2.3 seconds',
        triggerType: 'manual',
        canRollback: true
      }
      setImportHistory(prev => [newImport, ...prev])
      
    } catch (error) {
      console.error('Import failed:', error)
    } finally {
      setIsImporting(false)
    }
  }

  const handleRollback = async (importId: string) => {
    const confirmed = confirm('This will undo all changes from this import. Are you sure?')
    if (confirmed) {
      // Simulate rollback
      await new Promise(resolve => setTimeout(resolve, 1000))
      setImportHistory(prev => 
        prev.map(imp => 
          imp.id === importId ? { ...imp, canRollback: false } : imp
        )
      )
    }
  }

  const getStatusIcon = (status: typeof connectionStatus) => {
    switch (status) {
      case 'connected': return <CheckCircleIcon className="w-4 h-4 text-green-500" />
      case 'connecting': return <RefreshCwIcon className="w-4 h-4 text-blue-500 animate-spin" />
      case 'error': return <XCircleIcon className="w-4 h-4 text-red-500" />
      default: return <ClockIcon className="w-4 h-4 text-gray-400" />
    }
  }

  const getValidationMessage = (field: string) => {
    const result = validation[field]
    if (!result) return null
    return result.errors[0] || result.warnings[0] || null
  }

  const getValidationStatus = (field: string): 'default' | 'error' | 'warning' => {
    const result = validation[field]
    if (!result) return 'default'
    if (result.errors.length > 0) return 'error'
    if (result.warnings.length > 0) return 'warning'
    return 'default'
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-krushr-gray-dark">External Data Integration</h2>
        <Badge variant={connectionStatus === 'connected' ? 'default' : 'secondary'}>
          {getStatusIcon(connectionStatus)}
          <span className="ml-1">{connectionStatus}</span>
        </Badge>
      </div>

      <Tabs value={currentStep} onValueChange={(value) => setCurrentStep(value as any)} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="connection">Connection</TabsTrigger>
          <TabsTrigger value="config" disabled={connectionStatus !== 'connected'}>Configuration</TabsTrigger>
          <TabsTrigger value="mapping" disabled={connectionStatus !== 'connected'}>Mapping</TabsTrigger>
          <TabsTrigger value="schedule" disabled={connectionStatus !== 'connected'}>Schedule</TabsTrigger>
        </TabsList>

        <TabsContent value="connection" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Connection Setup</CardTitle>
              <CardDescription>Configure your external data source connection</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Service Type</label>
                  <Select 
                    value={connectionConfig.serviceType} 
                    onValueChange={(value: any) => 
                      setConnectionConfig(prev => ({ ...prev, serviceType: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="jetsy">Jetsy (Etsy Integration)</SelectItem>
                      <SelectItem value="shopify">Shopify</SelectItem>
                      <SelectItem value="custom">Custom API</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Display Name</label>
                  <Input
                    value={connectionConfig.displayName}
                    onChange={(e) => {
                      const value = e.target.value
                      setConnectionConfig(prev => ({ ...prev, displayName: value }))
                      validateField('displayName', value)
                    }}
                    placeholder="My External Service"
                    className={getValidationStatus('displayName') === 'error' ? 'border-red-500' : ''}
                  />
                  {getValidationMessage('displayName') && (
                    <p className={`text-xs ${getValidationStatus('displayName') === 'error' ? 'text-red-500' : 'text-yellow-500'}`}>
                      {getValidationMessage('displayName')}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">API Endpoint</label>
                <Input
                  value={connectionConfig.hostUrl}
                  onChange={(e) => {
                    const value = e.target.value
                    setConnectionConfig(prev => ({ ...prev, hostUrl: value }))
                    validateField('hostUrl', value)
                  }}
                  placeholder="https://api.example.com"
                  className={getValidationStatus('hostUrl') === 'error' ? 'border-red-500' : ''}
                />
                {getValidationMessage('hostUrl') && (
                  <p className={`text-xs ${getValidationStatus('hostUrl') === 'error' ? 'text-red-500' : 'text-yellow-500'}`}>
                    {getValidationMessage('hostUrl')}
                  </p>
                )}
              </div>

              <Collapsible 
                open={expandedSections.has('authentication')} 
                onOpenChange={(open) => {
                  setExpandedSections(prev => {
                    const next = new Set(prev)
                    if (open) next.add('authentication')
                    else next.delete('authentication')
                    return next
                  })
                }}
              >
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between p-0 h-auto">
                    <span className="text-sm font-medium">Authentication</span>
                    {expandedSections.has('authentication') ? (
                      <ChevronDownIcon className="w-4 h-4" />
                    ) : (
                      <ChevronRightIcon className="w-4 h-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Authentication Type</label>
                    <Select 
                      value={connectionConfig.authType} 
                      onValueChange={(value: any) => 
                        setConnectionConfig(prev => ({ ...prev, authType: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Authentication</SelectItem>
                        <SelectItem value="api-key">API Key</SelectItem>
                        <SelectItem value="oauth">OAuth 2.0</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {connectionConfig.authType === 'api-key' && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">API Key</label>
                      <Input
                        type="password"
                        value={connectionConfig.apiKey || ''}
                        onChange={(e) => {
                          const value = e.target.value
                          setConnectionConfig(prev => ({ ...prev, apiKey: value }))
                          validateField('apiKey', value)
                        }}
                        placeholder="Enter your API key"
                        className={getValidationStatus('apiKey') === 'error' ? 'border-red-500' : ''}
                      />
                      {getValidationMessage('apiKey') && (
                        <p className={`text-xs ${getValidationStatus('apiKey') === 'error' ? 'text-red-500' : 'text-yellow-500'}`}>
                          {getValidationMessage('apiKey')}
                        </p>
                      )}
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>
            </CardContent>
            <CardFooter>
              <Button 
                onClick={handleConnectionTest}
                disabled={connectionStatus === 'connecting' || !connectionConfig.hostUrl}
              >
                {connectionStatus === 'connecting' ? (
                  <>
                    <RefreshCwIcon className="w-4 h-4 mr-2 animate-spin" />
                    Testing Connection...
                  </>
                ) : (
                  'Test Connection'
                )}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="config" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Import Configuration</CardTitle>
              <CardDescription>Configure how data should be imported</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="dry-run" 
                  checked={importConfig.enableDryRun}
                  onCheckedChange={(checked) => 
                    setImportConfig(prev => ({ ...prev, enableDryRun: checked as boolean }))
                  }
                />
                <label htmlFor="dry-run" className="text-sm font-medium">
                  Enable dry run (preview only, don't create actual tasks)
                </label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Max Records</label>
                  <Input
                    type="number"
                    value={importConfig.maxRecords}
                    onChange={(e) => {
                      const value = parseInt(e.target.value)
                      setImportConfig(prev => ({ ...prev, maxRecords: value }))
                      validateField('maxRecords', value)
                    }}
                    min={1}
                    max={50000}
                    className={getValidationStatus('maxRecords') === 'error' ? 'border-red-500' : ''}
                  />
                  {getValidationMessage('maxRecords') && (
                    <p className={`text-xs ${getValidationStatus('maxRecords') === 'error' ? 'text-red-500' : 'text-yellow-500'}`}>
                      {getValidationMessage('maxRecords')}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Batch Size</label>
                  <Input
                    type="number"
                    value={importConfig.batchSize}
                    onChange={(e) => {
                      const value = parseInt(e.target.value)
                      setImportConfig(prev => ({ ...prev, batchSize: value }))
                      validateField('batchSize', value)
                    }}
                    min={1}
                    max={100}
                    className={getValidationStatus('batchSize') === 'error' ? 'border-red-500' : ''}
                  />
                  {getValidationMessage('batchSize') && (
                    <p className={`text-xs ${getValidationStatus('batchSize') === 'error' ? 'text-red-500' : 'text-yellow-500'}`}>
                      {getValidationMessage('batchSize')}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="skip-existing" 
                    checked={importConfig.skipExisting}
                    onCheckedChange={(checked) => 
                      setImportConfig(prev => ({ ...prev, skipExisting: checked as boolean }))
                    }
                  />
                  <label htmlFor="skip-existing" className="text-sm font-medium">
                    Skip existing records
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="download-images" 
                    checked={importConfig.enableImageDownload}
                    onCheckedChange={(checked) => 
                      setImportConfig(prev => ({ ...prev, enableImageDownload: checked as boolean }))
                    }
                  />
                  <label htmlFor="download-images" className="text-sm font-medium">
                    Download and attach images
                  </label>
                </div>
              </div>

              {importConfig.enableImageDownload && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Max Images Per Task</label>
                  <Input
                    type="number"
                    value={importConfig.maxImagesPerTask}
                    onChange={(e) => {
                      const value = parseInt(e.target.value)
                      setImportConfig(prev => ({ ...prev, maxImagesPerTask: value }))
                    }}
                    min={1}
                    max={10}
                  />
                </div>
              )}
            </CardContent>
            <CardFooter>
              <Button onClick={handleImport} disabled={isImporting}>
                {isImporting ? (
                  <>
                    <RefreshCwIcon className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  importConfig.enableDryRun ? 'Preview Import' : 'Start Import'
                )}
              </Button>
            </CardFooter>
          </Card>

          {isImporting && (
            <Card>
              <CardHeader>
                <CardTitle>Import Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Processing records...</span>
                    <span>{importProgress.current} / {importProgress.total}</span>
                  </div>
                  <Progress value={importProgress.percent} className="w-full" />
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="mapping" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Field Mapping</CardTitle>
              <CardDescription>Map external fields to task properties</CardDescription>
            </CardHeader>
            <CardContent>
              <Alert>
                <AlertTriangleIcon className="h-4 w-4" />
                <AlertDescription>
                  Field mapping interface would be implemented here based on the specific external service schema.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="schedule" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Import History</CardTitle>
              <CardDescription>View and manage previous imports</CardDescription>
            </CardHeader>
            <CardContent>
              {importHistory.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No imports yet</p>
              ) : (
                <div className="space-y-4">
                  {importHistory.map((importRun) => (
                    <div key={importRun.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge variant={importRun.status === 'success' ? 'default' : 'destructive'}>
                            {importRun.status}
                          </Badge>
                          <span className="text-sm text-gray-500">
                            {importRun.timestamp.toLocaleString()}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          Created: {importRun.recordsCreated} • Updated: {importRun.recordsUpdated} • Failed: {importRun.recordsFailed}
                        </div>
                        <div className="text-xs text-gray-500">
                          Duration: {importRun.duration} • Trigger: {importRun.triggerType}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {importRun.canRollback && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRollback(importRun.id)}
                          >
                            Rollback
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default ExternalDataIntegration